import { WebApiService } from '../../../plugins/webApiService'
import { Events } from '../../../plugins/events'
import EventToAsyncDecorator from '../../../plugins/eventToAsyncDecorator'
import SearchFilterRequest from '../../../Spirit/Admin/Interfaces/Models/Common/SearchFilterRequest';
import AffirmationUpdateRequest from '../../../Spirit/Admin/Interfaces/Models/AfirmationSerivce/AffirmationUpdateRequest';
import ChangeSortIndexRequest from '../../../Spirit/Common/Helpers/Models/ChangeSortIndexRequest';
import WsResponseModelT from '../../../SchemaBuilderServer/Models/WsResponseModelT';
import AffirmationGetDetailResponse from '../../../Spirit/Admin/Interfaces/Models/AfirmationSerivce/AffirmationGetDetailResponse';
import PaginationResponseT from '../../../Common/ResponseModels/PaginationResponseT';
import AffirmationGetListResponse from '../../../Spirit/Admin/Interfaces/Models/AfirmationSerivce/AffirmationGetListResponse';
import AppSettings from '@/appSettings';
import { readPaginationCount } from '@/utils/pagination';

export default class AffirmationService {
	webApiService: WebApiService;
	DeleteEvent = new Events<WsResponseModelT<Boolean>>();
	GetDetailEvent = new Events<WsResponseModelT<AffirmationGetDetailResponse>>();
	GetListEvent = new Events<WsResponseModelT<PaginationResponseT<AffirmationGetListResponse>>>();
	UpdateEvent = new Events<WsResponseModelT<Boolean>>();
	UpdateItemEvent = new Events<WsResponseModelT<string>>();
	DeleteItemEvent = new Events<WsResponseModelT<string>>();
	ChangeSortIndexEvent = new Events<WsResponseModelT<Boolean>>();
	constructor(webApiService: WebApiService) {
		const self = this;
		this.webApiService = webApiService;
		webApiService.on('AffirmationService', 'Delete', (res: WsResponseModelT<Boolean>) => {
			self.DeleteEvent.trigger(res);
		});
		webApiService.on('AffirmationService', 'GetDetail', (res: WsResponseModelT<AffirmationGetDetailResponse>) => {
			self.GetDetailEvent.trigger(res);
		});
		webApiService.on('AffirmationService', 'GetList', (res: WsResponseModelT<PaginationResponseT<AffirmationGetListResponse>>) => {
			self.GetListEvent.trigger(res);
		});
		webApiService.on('AffirmationService', 'Update', (res: WsResponseModelT<Boolean>) => {
			self.UpdateEvent.trigger(res);
		});
		webApiService.on('AffirmationService', 'UpdateItem', (res: WsResponseModelT<string>) => {
			self.UpdateItemEvent.trigger(res);
		});
		webApiService.on('AffirmationService', 'DeleteItem', (res: WsResponseModelT<string>) => {
			self.DeleteItemEvent.trigger(res);
		});
		webApiService.on('AffirmationService', 'ChangeSortIndex', (res: WsResponseModelT<Boolean>) => {
			self.ChangeSortIndexEvent.trigger(res);
		});
	}
	Delete(id: number = 0) { 	
        this.webApiService.send('AffirmationService', 'Delete' , id);
    }
	GetDetail(id: number = 0) { 	
        this.webApiService.send('AffirmationService', 'GetDetail' , id);
    }
	GetList(request: SearchFilterRequest = null) { 	
        this.webApiService.send('AffirmationService', 'GetList' , request);
    }
	Update(request: AffirmationUpdateRequest = null) { 	
        this.webApiService.send('AffirmationService', 'Update' , request);
    }
	UpdateItem(request: any = null) {
        this.webApiService.send('AffirmationService', 'UpdateItem' , request);
    }
	DeleteItem(request: any = null) {
        this.webApiService.send('AffirmationService', 'DeleteItem' , request);
    }
	ChangeSortIndex(request: ChangeSortIndexRequest = null) { 	
        this.webApiService.send('AffirmationService', 'ChangeSortIndex' , request);
    }
	 // async methods
	async deleteAsync(id: number = 0): Promise<Boolean> {
		return EventToAsyncDecorator.getInstance().bind(this.Delete.bind(this), this.DeleteEvent)(id);
	}
	async getDetailAsync(id: number = 0): Promise<AffirmationGetDetailResponse> {
		const response = await EventToAsyncDecorator.getInstance().bind(this.GetDetail.bind(this), this.GetDetailEvent)(id);
		return this.normalizeDetail(response);
	}
	async getListAsync(request: SearchFilterRequest = null): Promise<PaginationResponseT<AffirmationGetListResponse>> {
		const response = await EventToAsyncDecorator.getInstance().bind(this.GetList.bind(this), this.GetListEvent)(request);
		return new PaginationResponseT<AffirmationGetListResponse>({
			Count: readPaginationCount(response),
			Items: (response?.Items || []).map((item) => this.normalizeListItem(item)),
		});
	}
	async updateAsync(request: AffirmationUpdateRequest = null): Promise<Boolean> {
		return EventToAsyncDecorator.getInstance().bind(this.Update.bind(this), this.UpdateEvent)(request);
	}
	async updateItemAsync(request: any = null): Promise<string> {
		return this.webApiService.requestHttpAsync('/api/affirmation/item', 'POST', request);
	}
	async deleteItemAsync(request: any = null): Promise<string> {
		return EventToAsyncDecorator.getInstance().bind(this.DeleteItem.bind(this), this.DeleteItemEvent)(request);
	}
	async changeSortIndexAsync(request: ChangeSortIndexRequest = null): Promise<Boolean> {
		return EventToAsyncDecorator.getInstance().bind(this.ChangeSortIndex.bind(this), this.ChangeSortIndexEvent)(request);
	}

	private normalizeListItem(item: AffirmationGetListResponse): AffirmationGetListResponse {
		if (!item) {
			return item;
		}

		return new AffirmationGetListResponse({
			...item,
			CoverUrl: this.normalizeFileUrl(item.CoverUrl),
			BackUrl: this.normalizeFileUrl(item.BackUrl),
			IconUrl: this.normalizeFileUrl(item.IconUrl),
			AnimationUrl: this.normalizeFileUrl(item.AnimationUrl),
			Translations: this.normalizeTranslations(item.Translations),
		});
	}

	private normalizeDetail(item: AffirmationGetDetailResponse): AffirmationGetDetailResponse {
		if (!item) {
			return item;
		}

		return new AffirmationGetDetailResponse({
			...item,
			CoverUrl: this.normalizeFileUrl(item.CoverUrl),
			BackUrl: this.normalizeFileUrl(item.BackUrl),
			IconUrl: this.normalizeFileUrl(item.IconUrl),
			AnimationUrl: this.normalizeFileUrl(item.AnimationUrl),
			Translations: this.normalizeTranslations(item.Translations),
		});
	}

	private normalizeTranslations(translations: any[] = []): any[] {
		return (translations || []).map((translation) => ({
			...translation,
			VoiceFiles: (translation.VoiceFiles || []).map((voiceFile) => ({
				...voiceFile,
				Url: this.normalizeFileUrl(voiceFile.Url),
				FemaleUrl: this.normalizeFileUrl(voiceFile.FemaleUrl),
				MaleUrl: this.normalizeFileUrl(voiceFile.MaleUrl),
			})),
		}));
	}

	private normalizeFileUrl(url?: string): string {
		if (!url) {
			return url;
		}

		const trimmedUrl = url.trim();
		if (!trimmedUrl) {
			return trimmedUrl;
		}

		if (/^https?:\/\//i.test(trimmedUrl) || /^blob:/i.test(trimmedUrl) || /^data:/i.test(trimmedUrl)) {
			return trimmedUrl;
		}

		if (trimmedUrl.startsWith('/files/')) {
			return `${AppSettings.filesBaseUrl}${trimmedUrl}`;
		}

		if (trimmedUrl.startsWith('files/')) {
			return `${AppSettings.filesBaseUrl}/${trimmedUrl}`;
		}

		if (trimmedUrl.startsWith('/')) {
			return `${AppSettings.filesBaseUrl}${trimmedUrl}`;
		}

		const lowerCasedUrl = trimmedUrl.toLowerCase();
		const looksLikeVideoFile = lowerCasedUrl.endsWith('.mp4') || lowerCasedUrl.endsWith('.webm') || lowerCasedUrl.endsWith('.mov');
		if (looksLikeVideoFile) {
			return `${AppSettings.filesBaseUrl}/files/Animations/${trimmedUrl}`;
		}

		return `${AppSettings.filesBaseUrl}/${trimmedUrl}`;
	}
}
